"use client";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CacheKeys } from "../../constants/CacheKeys";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import { useGetImageFields } from "../../hooks/useImages";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Image } from "../../models/Image";
import { mergeImagesFieldsWithAssociatedActivitiesFields } from "../../services/ImageService";
import { ReportType } from "../../services/ReportService";
import { getImageColumnsFromColumnOrder } from "../../services/ViewService";
import { MassCreateDraftButton } from "../list/MassCreateDraftButton";
import { PaginatedList } from "../list/PaginatedList";

const PublishedActivityImagesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onMassCreateDraftSuccess = (createdVersionIds: string[]) => {
    window.sessionStorage.setItem(
      SelectionPersistenceKeys.ImagesActivityDrafts,
      JSON.stringify(createdVersionIds)
    );
    navigate(`${ClientRoutes.Media}?view=drafts`);
  };

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetImageFields();
  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const handleEdit = useCallback(
    (imageId: string) => {
      navigate(`${ClientRoutes.MediaActivityEdit}/overview/${imageId}`);
    },
    [navigate]
  );

  const columns = useMemo(() => {
    if (
      viewDefinitionLoading ||
      !viewDefinition ||
      !activityViewDefinition ||
      activityViewDefinitionLoading
    ) {
      return [];
    }
    return getImageColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      activityViewDefinition.fields,
      viewDefinition.publishedFieldOrder ?? [],
      handleEdit,
      true, // Activity context
      false // Published context (not draft)
    );
  }, [
    viewDefinition,
    viewDefinitionLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
    handleEdit,
  ]);

  if (viewDefinitionLoading || activityViewDefinitionLoading) {
    return null;
  }

  return (
    <PaginatedList<Image>
      apiEndpoint={ApiEndpoints.ImageCurrent}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      columns={columns}
      searchPlaceholder={t("searchImage")}
      postQueryTransformation={mergeImagesFieldsWithAssociatedActivitiesFields}
      cacheKey={CacheKeys.Image.ImageList}
      reportType={ReportType.PublishedProcessImages}
      filtersEndpoint={ApiEndpoints.ImageFilters}
      storageKey="images:activity:published"
      libraryFilterScope={{ listType: "image" }}
      selectedActions={(selectedVersionIds) => (
        <MassCreateDraftButton
          versionIds={selectedVersionIds}
          entityType="image"
          onSuccess={onMassCreateDraftSuccess}
        />
      )}
    />
  );
};

export default PublishedActivityImagesList;
